import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  Alert,
  Tabs,
  Tab,
  Stack,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import AppCircularProgress from "../components/common/AppCircularProgress";
import AppButton from "../components/buttons/AppButton";
import StatusChip from "../components/common/StatusChip";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InventoryIcon from "@mui/icons-material/Inventory";
import PaymentIcon from "@mui/icons-material/Payment";
import MoreVertIcon from "@mui/icons-material/MoreVert";

import useWorkspaceNavigation from "../hooks/useWorkspaceNavigation";
import {
  getPurchaseOrderById,
  payPurchaseOrder,
} from "../redux/actions/purchaseOrderActions";
import { purchaseOrderActions } from "../redux/slices/purchaseOrder";
import PurchaseOrderDetailsTab from "../components/purchaseOrders/PurchaseOrderDetailsTab";
import PurchaseOrderPdfTab from "../components/purchaseOrders/PurchaseOrderPdfTab";

import ConfirmPoModal from "../components/purchaseOrders/ConfirmPoModal";
import ReceivePoModal from "../components/purchaseOrders/ReceivePoModal";
import RecordPaymentModal from "../components/payments/RecordPaymentModal";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { PaymentRequestDto } from "../shared/dtos/payment/PaymentRequestDto";
import { PurchaseOrderStatus } from "../shared/enums/PurchaseOrderStatus";

const PurchaseOrderPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { closeCurrentAndNavigate } = useWorkspaceNavigation();

  const {
    item: currentOrder,
    loadingFetchOne: loadingOrder,
    errorFetchOne: errorLoadingOrder,
    serverStatus,
    loadingPay,
    errorPay,
  } = useAppSelector((state) => state.purchaseOrder);

  const [activeTab, setActiveTab] = useState<number>(0);
  const [poId, setPoId] = useState<string | null>(null);
  const [modalState, setModalState] = useState<
    "CONFIRM" | "RECEIVE" | "PAY" | null
  >(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const uuidRegex =
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
    const match = location.pathname.match(uuidRegex);

    if (match && match[0]) {
      setPoId(match[0]);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (poId) dispatch(getPurchaseOrderById(poId));
  }, [dispatch, poId]);

  useEffect(() => {
    if (serverStatus?.status === 200) {
      setModalState(null);
      dispatch(purchaseOrderActions.setServerStatus(null));
    }
  }, [serverStatus, dispatch]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>): void =>
    setMenuAnchorEl(event.currentTarget);
  const handleMenuClose = (): void => setMenuAnchorEl(null);

  const openModal = (type: "CONFIRM" | "RECEIVE" | "PAY"): void => {
    handleMenuClose();
    setModalState(type);
  };

  const handleRecordPayment = (
    id: string,
    payload: PaymentRequestDto,
  ): void => {
    dispatch(payPurchaseOrder(id, payload));
  };

  if (loadingOrder) return <AppCircularProgress />;
  if (errorLoadingOrder)
    return <Alert severity="error">{errorLoadingOrder}</Alert>;
  if (!currentOrder) return null;

  const isPending = currentOrder.status === PurchaseOrderStatus.PENDING;
  const canReceive =
    currentOrder.status === PurchaseOrderStatus.APPROVED ||
    currentOrder.status === PurchaseOrderStatus.PARTIALLY_RECEIVED;
  const canPay =
    currentOrder.status === PurchaseOrderStatus.APPROVED ||
    (currentOrder.status === PurchaseOrderStatus.FULLY_RECEIVED &&
      Number(currentOrder.balanceAmount || 0) > 0);

  const renderDesktopActions = (): React.JSX.Element => (
    <Stack direction="row" spacing={2}>
      {isPending && (
        <AppButton
          pill
          variant="contained"
          color="primary"
          startIcon={<CheckCircleIcon />}
          onClick={() => openModal("CONFIRM")}
        >
          Confirm Order
        </AppButton>
      )}
      {canReceive && (
        <AppButton
          pill
          variant="contained"
          color="secondary"
          startIcon={<InventoryIcon />}
          onClick={() => openModal("RECEIVE")}
        >
          Receive Goods
        </AppButton>
      )}
      {canPay && (
        <AppButton
          pill
          variant="outlined"
          color="warning"
          startIcon={<PaymentIcon />}
          onClick={() => openModal("PAY")}
        >
          Record Payment
        </AppButton>
      )}
    </Stack>
  );

  const renderMobileActions = (): React.JSX.Element => (
    <Box>
      <IconButton onClick={handleMenuOpen} color="inherit">
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        {isPending && (
          <MenuItem onClick={() => openModal("CONFIRM")}>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText primary="Confirm Order" />
          </MenuItem>
        )}
        {canReceive && (
          <MenuItem onClick={() => openModal("RECEIVE")}>
            <ListItemIcon>
              <InventoryIcon fontSize="small" color="secondary" />
            </ListItemIcon>
            <ListItemText primary="Receive Goods" />
          </MenuItem>
        )}
        {canPay && (
          <MenuItem onClick={() => openModal("PAY")}>
            <ListItemIcon>
              <PaymentIcon fontSize="small" color="warning" />
            </ListItemIcon>
            <ListItemText primary="Record Payment" />
          </MenuItem>
        )}
      </Menu>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "background.default",
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          pt: 3,
          pb: 1,
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Stack
          direction="row"
          sx={{ justifyContent: "space-between", alignItems: "center", mb: 2 }}
        >
          <Stack direction="row" sx={{ alignItems: "center" }} spacing={2}>
            <IconButton
              onClick={() => closeCurrentAndNavigate("/purchase-orders")}
              color="inherit"
              size="small"
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography
              variant={isMobile ? "h6" : "h5"}
              sx={{ fontWeight: "bold" }}
            >
              PO #{currentOrder.orderNumber || currentOrder.id.substring(0, 8)}
            </Typography>
            {!isMobile && <StatusChip status={currentOrder.status} />}
          </Stack>

          {isMobile ? renderMobileActions() : renderDesktopActions()}
        </Stack>

        {isMobile && (
          <Box sx={{ mb: 2, pl: 5 }}>
            <StatusChip status={currentOrder.status} />
          </Box>
        )}

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ ml: { xs: 0, sm: 6 }, mb: 2 }}
        >
          Created:{" "}
          {currentOrder.orderDate
            ? new Date(currentOrder.orderDate).toLocaleString()
            : "N/A"}{" "}
          • {currentOrder.branchName || ""}
        </Typography>

        <Tabs
          value={activeTab}
          onChange={(_e, v) => setActiveTab(v)}
          indicatorColor="primary"
          textColor="primary"
          variant={isMobile ? "fullWidth" : "standard"}
        >
          <Tab label="Order Details & Status" />
          <Tab label="Purchase Order PDF" />
        </Tabs>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", p: { xs: 1.5, sm: 3 } }}>
        {activeTab === 0 && <PurchaseOrderDetailsTab order={currentOrder} />}
        {activeTab === 1 && <PurchaseOrderPdfTab order={currentOrder} />}
      </Box>

      {modalState === "CONFIRM" && (
        <ConfirmPoModal
          open={true}
          onClose={() => setModalState(null)}
          order={currentOrder}
        />
      )}
      {modalState === "RECEIVE" && (
        <ReceivePoModal
          open={true}
          onClose={() => setModalState(null)}
          order={currentOrder}
        />
      )}
      {modalState === "PAY" && (
        <RecordPaymentModal
          open={true}
          onClose={() => setModalState(null)}
          entityId={currentOrder.id}
          entityType="Purchase Order"
          documentNumber={
            currentOrder.orderNumber || currentOrder.id.substring(0, 8)
          }
          balance={Number(currentOrder.balanceAmount || 0)}
          loading={loadingPay}
          error={errorPay}
          onSubmitPayment={handleRecordPayment}
        />
      )}
    </Box>
  );
};

export default PurchaseOrderPage;
